import { Radio, RadioGroup } from 'taboola-ultimate-ui';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { CONVERSION_TYPE } from 'modules/tracking/config/conversionType';
import { STEP_IDS } from '../../config/stepIds';
import { useSetupValue } from '../../hooks/useSetupValue';
import { NewEventHeader } from '../Headers/NewEventHeader';
import { SetupValueFooter } from './components/SetupValueFooter/SetupValueFooter';
import { ValueHighlighterInstructions } from './components/ValueHighlighterInstructions/ValueHighlighterInstructions';
import { ValueSelector } from './components/ValueSelector/ValueSelector';
import styles from './setupValue.module.scss';

export const SetupValue = ({
    currentConversion,
    navigateToStepId,
    handleCancelWizard,
    updateDraftEvent,
    handleSaveDraftToPersistent,
}) => {
    const isValueFeatureEnabled = useConfigMatch({ [FEATURE_FLAGS.CODELESS_VALUE_ENABLED]: 'true' });

    const {
        includeValue,
        isHighlighting,
        selectedValue,
        canCreateEvent,
        handleRadioChange,
        handleEditValue,
        setIsHighlighting,
        setFocusedConversionId,
    } = useSetupValue({ currentConversion, handleCancelWizard, updateDraftEvent });

    const unhighlight = () => setIsHighlighting(false);

    const handleGoBack = () => {
        const stepId =
            currentConversion?.type === CONVERSION_TYPE.EVENT_BASED
                ? STEP_IDS.SETUP_CLICKS_EVENT_CATEGORY
                : STEP_IDS.SETUP_URL_EVENT;
        navigateToStepId(stepId);
    };

    if (!isValueFeatureEnabled) {
        handleSaveDraftToPersistent();
        setFocusedConversionId(undefined);
        return null;
    }

    if (isHighlighting) {
        return <ValueHighlighterInstructions onClose={unhighlight} />;
    }

    return (
        <>
            <NewEventHeader handlerArrowLeftButton={handleGoBack} />
            <div>
                <div className={styles['setup-title']}>
                    <FormattedMessage
                        id="codeless.setup.clicks.value.header"
                        defaultMessage="Setup button clicks event"
                    />
                </div>
                <div className={styles['setup-container']}>
                    <div className={styles['setup-subtitle']}>
                        <FormattedMessage id="codeless.setup.clicks.value.subtitle" defaultMessage="Value" />
                    </div>
                    <div className={styles['setup-description']}>
                        <FormattedMessage
                            id="codeless.setup.value.description"
                            defaultMessage="Set up order value or product price to measure and optimize our return on ads spend"
                        />
                    </div>
                    <div className={styles['radio-group']}>
                        <RadioGroup
                            selectedValue={includeValue ? 'select' : 'dont_include'}
                            onChange={handleRadioChange}
                        >
                            <Radio
                                value="select"
                                title={
                                    <FormattedMessage
                                        id="codeless.setup.value.select"
                                        defaultMessage="Select value on page"
                                    />
                                }
                            />
                            {includeValue && (
                                <div className={styles['value-section']}>
                                    <ValueSelector
                                        selectedValue={selectedValue}
                                        onSelect={() => setIsHighlighting(true)}
                                        onEdit={handleEditValue}
                                    />
                                </div>
                            )}
                            <Radio
                                value="dont_include"
                                title={
                                    <FormattedMessage
                                        id="codeless.setup.value.dont_include"
                                        defaultMessage="Don't include value"
                                    />
                                }
                            />
                        </RadioGroup>
                    </div>
                </div>
            </div>
            <SetupValueFooter
                onCancel={handleCancelWizard}
                onNext={handleSaveDraftToPersistent}
                isNextDisabled={!canCreateEvent}
            />
        </>
    );
};
